import React, { createContext, useContext, ReactNode, useEffect, useMemo } from 'react';
import ActionRequiredService, {
  ActionDecisionMethod,
  ActionPath,
  IActionRequiredService,
  useHasAction as useHasActionHook,
} from './services/actionRequiredService';

interface ActionRequiredContextType<T extends Record<string, any>> {
  actionService: IActionRequiredService<T>;
}

const ActionRequiredContext =
  createContext<ActionRequiredContextType<any> | undefined>(undefined);

interface ActionRequiredProviderProps<T extends Record<string, any>> {
  children: ReactNode;
  context: T; // Context data for decision methods
  customDecisionMethods: ActionDecisionMethod<T>[]; // Required custom methods
}

const ActionRequiredProvider = <T extends Record<string, any>>({
  children,
  context,
  customDecisionMethods,
}: ActionRequiredProviderProps<T>) => {
  // Create action service instance
  const actionService = useMemo(() => {
    const service = new ActionRequiredService<T>();

    // Register only custom decision methods
    customDecisionMethods.forEach((method) => {
      service.registerDecisionMethod(method);
    });

    return service;
  }, [customDecisionMethods]);

  // Update actions when context changes
  useEffect(() => {
    actionService.updateActions(context);
  }, [context, actionService]);

  const value: ActionRequiredContextType<T> = useMemo(
    () => ({
      actionService,
    }),
    [actionService],
  );

  return (
    <ActionRequiredContext.Provider value={value}>
      {children}
    </ActionRequiredContext.Provider>
  );
};

export default ActionRequiredProvider;
// Custom hook to use action required context
export function useHasAction(path: ActionPath): boolean {
  const context = useContext(ActionRequiredContext);
  if (context === undefined) {
    throw new Error('useActionRequired must be used within an ActionRequiredProvider');
  }
  return useHasActionHook(context.actionService, path);
}

// Helper hook to get action service
export function useActionService<
  T extends Record<string, any> = any,
>(): IActionRequiredService<T> {
  const context = useContext(ActionRequiredContext);
  if (context === undefined) {
    throw new Error('useActionService must be used within an ActionRequiredProvider');
  }
  return context.actionService;
}
